import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { CLEANING_TYPE_FROM_NUMBER, CLEANING_TYPE_TO_NUMBER, CleaningTypeEnum } from 'api/schema'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import { ReactComponent as ChargingSvg } from 'images/icons/plug_in.svg'
import cloneDeep from 'lodash.clonedeep'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectRobot, selectRobotTelemetry } from 'store/selectors/robot'
import {
  ROBOT_EQUIPMENT_COMMAND,
  ROBOT_EQUIPMENT_STATES,
  ROBOT_NETWORK_STATUSES,
  SCRUBBER_BRUSH_RPM_MAX,
  SCRUBBER_BRUSH_RPM_MIN,
  TURBINE_RPM_MAX,
  TURBINE_RPM_MIN,
} from 'utils/constants'
import { isComboOrOmniRobotType } from 'utils/robotTypeDisplayName'
import { readLiveExecutionFromRobotRead } from 'utils/robotLiveExecution'
import { VENDOR } from 'utils/vendorDisplayName'

import MotorRpmSlider from './MotorRpmSlider'
import SwitchStyled from './SwitchStyled'

import style from './RobotControl.module.css'

type Status = {
  blocked: boolean
  value: boolean
}

type Statuses = {
  [key in ROBOT_EQUIPMENT_COMMAND]: Status
}

const defaultStatuses: Statuses = {
  // [ROBOT_EQUIPMENT_COMMAND.TRASH]: {
  //   blocked: false,
  //   value: false,
  // },
  [ROBOT_EQUIPMENT_COMMAND.BODY]: {
    blocked: false,
    value: false,
  },
  // [ROBOT_EQUIPMENT_COMMAND.FRONT]: {
  //   blocked: false,
  //   value: false,
  // },
  // [ROBOT_EQUIPMENT_COMMAND.CHARGE]: {
  //   blocked: false,
  //   value: false,
  // },
  [ROBOT_EQUIPMENT_COMMAND.WATER]: {
    blocked: false,
    value: false,
  },
  [ROBOT_EQUIPMENT_COMMAND.LIGHT]: {
    blocked: false,
    value: false,
  },
  [ROBOT_EQUIPMENT_COMMAND.DANGER]: {
    blocked: false,
    value: false,
  },
  [ROBOT_EQUIPMENT_COMMAND.CLEANING_EQUIPMENT]: {
    blocked: false,
    value: false,
  },
}

const cleaningModeOptions: IOption[] = (
  [CleaningTypeEnum.SWEEPER, CleaningTypeEnum.SCRUBBER, CleaningTypeEnum.COMBO] as const
).map(type => ({
  text: type,
  value: type,
}))

const RobotControl = () => {
  const robot = useSelector(selectRobot)
  const telemetry = useSelector(selectRobotTelemetry)
  const [statuses, setStatuses] = useState<Statuses>(defaultStatuses)
  const statusesRef = useRef(statuses)
  statusesRef.current = statuses
  const [chargingInFlight, setChargingInFlight] = useState(false)
  const [confirmGausiumChargingDialog, setConfirmGausiumChargingDialog] = useState(false)
  const [gausiumMapsLoading, setGausiumMapsLoading] = useState(false)
  const [gausiumMaps, setGausiumMaps] = useState<
    Array<{
      id: number
      vendor_map_id: string
      map_name: string
      charging_station_pose?: Record<string, unknown> | null
    }>
  >([])
  const [selectedCleaningMode, setSelectedCleaningMode] = useState<CleaningTypeEnum>(CleaningTypeEnum.COMBO)
  const [cleaningModeBlocked, setCleaningModeBlocked] = useState(false)
  const [motorRpmBlocked, setMotorRpmBlocked] = useState(false)
  const lastTelemetryCleaningMode = useRef<CleaningTypeEnum | undefined>(undefined)
  const isEmergencyStatusLocked =
    telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY ||
    robot.robotInfo?.robotRaw?.equipment_state === ROBOT_EQUIPMENT_STATES.EMERGENCY

  useEffect(() => {
    const newStatuses = cloneDeep(statuses)
    // if (!statuses.trash_lock.blocked && telemetry?.locks?.trash !== undefined) {
    //   newStatuses.trash_lock.value = telemetry?.locks?.trash
    // }
    if (!statuses.body_lock.blocked && telemetry?.locks?.body !== undefined) {
      newStatuses.body_lock.value = telemetry?.locks?.body
    }
    // if (!statuses.front_lock.blocked && telemetry?.locks?.front !== undefined) {
    //   newStatuses.front_lock.value = telemetry?.locks?.front
    // }
    // if (!statuses.charge_lock.blocked && telemetry?.locks?.charge !== undefined) {
    //   newStatuses.charge_lock.value = telemetry?.locks?.charge
    // }

    if (!statuses.cleaning_equipment_command.blocked && telemetry?.cleaningEquipmentState !== undefined) {
      newStatuses.cleaning_equipment_command.value = telemetry?.cleaningEquipmentState === 1
    }

    if (!statuses.watering_command.blocked && telemetry?.locks?.watering !== undefined) {
      newStatuses.watering_command.value = telemetry?.locks?.watering
    }
    if (!statuses.light_command.blocked && telemetry?.locks?.light !== undefined) {
      newStatuses.light_command.value = telemetry?.locks?.light
    }
    if (!statuses.danger_button_command.blocked) {
      newStatuses.danger_button_command.value = isEmergencyStatusLocked
    }
    setStatuses(newStatuses)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telemetry?.locks, telemetry?.cleaningEquipmentState, robot.robotInfo?.robotRaw?.equipment_state])

  const isOnline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE
  const isComboOrOmniRobot = isComboOrOmniRobotType(robot.robotInfo?.robot_type)
  const motorControlsDisabled = !isOnline || isEmergencyStatusLocked || motorRpmBlocked

  const onMotorRpmCommitted = async (device: 'turbine' | 'scrubber_brush', rpm: number) => {
    if (!robot.robotInfo?.id || motorControlsDisabled) {
      return
    }
    setMotorRpmBlocked(true)
    try {
      await RobotApi.execEquipmentDeviceSettings(robot.robotInfo.id, device, rpm)
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to set motor rpm.'
      window.alert(msg)
    } finally {
      setTimeout(() => setMotorRpmBlocked(false), 1000)
    }
  }

  const telemetryCleaningMode = useMemo(() => {
    const workingType = telemetry?.workingType
    if (workingType === undefined || workingType === null || workingType === 0) {
      return undefined
    }
    return CLEANING_TYPE_FROM_NUMBER[workingType]
  }, [telemetry?.workingType])

  const showCleaningModeControl = isComboOrOmniRobot && telemetryCleaningMode !== undefined

  const cleaningModeOption = useMemo(() => {
    return (
      cleaningModeOptions.find(option => option.value === selectedCleaningMode) ||
      cleaningModeOptions.find(option => option.value === CleaningTypeEnum.COMBO) ||
      cleaningModeOptions[0]
    )
  }, [selectedCleaningMode])

  useEffect(() => {
    if (!showCleaningModeControl || cleaningModeBlocked) {
      return
    }
    if (!telemetryCleaningMode) {
      return
    }
    if (lastTelemetryCleaningMode.current !== telemetryCleaningMode) {
      if (
        selectedCleaningMode === CleaningTypeEnum.COMBO ||
        selectedCleaningMode === lastTelemetryCleaningMode.current
      ) {
        setSelectedCleaningMode(telemetryCleaningMode)
      }
      lastTelemetryCleaningMode.current = telemetryCleaningMode
    }
  }, [showCleaningModeControl, cleaningModeBlocked, telemetryCleaningMode, selectedCleaningMode])

  const onCleaningModeSelected = async (option: IOption) => {
    const value = option.value as CleaningTypeEnum
    if (!robot.robotInfo?.id || !isOnline || cleaningModeBlocked) {
      return
    }
    const workingType = CLEANING_TYPE_TO_NUMBER[value]
    if (telemetry?.workingType === workingType) {
      setSelectedCleaningMode(value)
      return
    }

    setSelectedCleaningMode(value)
    setCleaningModeBlocked(true)
    try {
      await RobotApi.execEquipmentWorkingType(robot.robotInfo.id, workingType)
    } catch (error) {
      if (telemetryCleaningMode) {
        setSelectedCleaningMode(telemetryCleaningMode)
      }
      const msg = error instanceof Error ? error.message : 'Failed to switch cleaning mode.'
      window.alert(msg)
    } finally {
      setTimeout(() => setCleaningModeBlocked(false), 5000)
    }
  }

  const onLockChanged = async (command: ROBOT_EQUIPMENT_COMMAND, value: boolean) => {
    if (!robot.robotInfo?.id) {
      return
    }
    const newStatuses = cloneDeep(statusesRef.current)
    newStatuses[command].value = value
    newStatuses[command].blocked = true
    setStatuses(newStatuses)
    await RobotApi.execEquipmentCommand(robot.robotInfo?.id, command, value ? '1' : '2')
    setTimeout(function () {
      const newStatuses = cloneDeep(statusesRef.current)
      newStatuses[command].blocked = false
      setStatuses(newStatuses)
    }, 5000)
  }

  const isGausium = robot.robotInfo?.vendor === VENDOR.GAUSIUM
  const liveExecution = robot.robotInfo?.robotRaw
    ? readLiveExecutionFromRobotRead(robot.robotInfo.robotRaw as any)
    : undefined
  const currentRobotId = robot.robotInfo?.id

  useEffect(() => {
    if (!isGausium || !currentRobotId) {
      setGausiumMaps([])
      return
    }
    let cancelled = false
    setGausiumMapsLoading(true)
    MainApi.fetchGausiumMaps([currentRobotId])
      .then(({ results }) => {
        if (cancelled) return
        setGausiumMaps(results || [])
      })
      .catch(() => {
        if (!cancelled) setGausiumMaps([])
      })
      .finally(() => {
        if (!cancelled) setGausiumMapsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isGausium, currentRobotId])

  const liveMapPk = liveExecution?.map?.id
  const liveMapExternalId = String(liveExecution?.map?.external_id || '').trim()
  const liveMapName = String(liveExecution?.map?.name || '').trim()
  const currentGausiumMap = useMemo(() => {
    if (!isGausium) return undefined
    return (
      (liveMapPk != null ? gausiumMaps.find(m => Number(m.id) === Number(liveMapPk)) : undefined) ||
      (liveMapExternalId
        ? gausiumMaps.find(m => String(m.vendor_map_id || '').trim() === liveMapExternalId)
        : undefined)
    )
  }, [isGausium, liveMapPk, liveMapExternalId, gausiumMaps])
  const chargingPointName =
    currentGausiumMap?.charging_station_pose &&
    typeof currentGausiumMap.charging_station_pose === 'object' &&
    !Array.isArray(currentGausiumMap.charging_station_pose) &&
    typeof (currentGausiumMap.charging_station_pose as Record<string, unknown>).pointName === 'string'
      ? String((currentGausiumMap.charging_station_pose as Record<string, unknown>).pointName || '').trim()
      : ''
  const navStatus = String(liveExecution?.nav_status || '')
    .trim()
    .toUpperCase()
  const acceptedNavMap = String(liveExecution?.accepted_navigation_map || '').trim()
  const acceptedNavPosition = String(liveExecution?.accepted_navigation_position || '').trim()
  const isChargingNavigateInProgress =
    navStatus === 'NAVI_RUNNING' &&
    Boolean(acceptedNavMap) &&
    Boolean(acceptedNavPosition) &&
    acceptedNavMap === liveMapName &&
    acceptedNavPosition === chargingPointName

  const canStartGausiumCharging =
    isOnline &&
    !chargingInFlight &&
    !gausiumMapsLoading &&
    !isChargingNavigateInProgress &&
    Boolean(liveMapName) &&
    Boolean(chargingPointName)

  const onStartGausiumChargingClicked = async () => {
    if (!robot.robotInfo?.id || !canStartGausiumCharging) return
    const robotId = robot.robotInfo.id

    setChargingInFlight(true)
    try {
      await MainApi.postGausiumRemoteCommand(robotId, {
        kind: 'cross_navigate',
        map_name: liveMapName,
        navigate_point: chargingPointName,
        start_delay: 1,
      })
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to send GS charging command.'
      window.alert(msg)
    } finally {
      setChargingInFlight(false)
    }
  }

  const onGausiumEmergencyChanged = async (value: boolean) => {
    if (!robot.robotInfo?.id || !isOnline) return
    const newStatuses = cloneDeep(statusesRef.current)
    newStatuses.danger_button_command.value = value
    newStatuses.danger_button_command.blocked = true
    setStatuses(newStatuses)
    try {
      await MainApi.postGausiumRemoteCommand(robot.robotInfo.id, {
        kind: 'emergency_stop',
        emergency_enabled: value,
        start_delay: 1,
      })
    } catch (error) {
      const rollback = cloneDeep(statusesRef.current)
      rollback.danger_button_command.value = !value
      setStatuses(rollback)
      const msg = error instanceof Error ? error.message : 'Failed to send GS emergency command.'
      window.alert(msg)
    } finally {
      setTimeout(() => {
        const unblocked = cloneDeep(statusesRef.current)
        unblocked.danger_button_command.blocked = false
        setStatuses(unblocked)
      }, 1200)
    }
  }

  const emergencyRow = (
    <div className={style.tableLockRow}>
      <div className={style.textAlignLeft}>Emergency stop</div>
      <div className={style.alignRight}>
        <SwitchStyled
          checked={statuses.danger_button_command.value}
          disabled={statuses.danger_button_command.blocked}
          onChange={e => onLockChanged(ROBOT_EQUIPMENT_COMMAND.DANGER, e.target.checked)}
        />
      </div>
    </div>
  )

  const emergencyRowGausium = (
    <div className={style.tableLockRow}>
      <div className={style.textAlignLeft}>Emergency stop</div>
      <div className={style.alignRight}>
        <SwitchStyled
          checked={Boolean(statuses.danger_button_command.value)}
          disabled={statuses.danger_button_command.blocked || !isOnline}
          onChange={e => onGausiumEmergencyChanged(e.target.checked)}
        />
      </div>
    </div>
  )

  if (isGausium) {
    return (
      <div className={style.root}>
        <div className={style.tableRow}>
          <div className={style.centered}>
            <ChargingSvg />
          </div>
          <div className={clsx(style.chipSmallFont, style.centered)}>{!canStartGausiumCharging ? 'N/A' : ''}</div>
          <div className={clsx(style.chip, style.centered)}>
            <Button.Standart
              className={style.button}
              disabled={!canStartGausiumCharging}
              onClick={() => setConfirmGausiumChargingDialog(true)}
            >
              {chargingInFlight ? 'Sending...' : 'Start Charging'}
            </Button.Standart>
          </div>
        </div>
        {emergencyRowGausium}
        <ConfirmDialog
          title={'Start charging?'}
          isOpen={confirmGausiumChargingDialog}
          onClose={() => setConfirmGausiumChargingDialog(false)}
          onConfirmClick={async () => {
            setConfirmGausiumChargingDialog(false)
            await onStartGausiumChargingClicked()
          }}
          confirmText={'Confirm'}
          danger={false}
        />
      </div>
    )
  }

  return (
    <div className={style.root}>
      <div className={style.tableLockRow}>
        <div className={style.textAlignLeft}>Body lock</div>
        <div className={style.alignRight}>
          <SwitchStyled
            checked={!statuses.body_lock.value}
            onChange={e => onLockChanged(ROBOT_EQUIPMENT_COMMAND.BODY, !e.target.checked)}
            disabled={statuses.body_lock.blocked}
          />
        </div>
      </div>
      <div className={style.tableLockRow}>
        <div className={style.textAlignLeft}>
          {telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY ? (
            <span className={style.bold}>Cleaning equipment (EMERGENCY)</span>
          ) : (
            <>Cleaning equipment</>
          )}
        </div>
        <div className={style.alignRight}>
          <SwitchStyled
            checked={
              telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY
                ? false
                : !statuses.cleaning_equipment_command.value
            }
            disabled={
              statuses.cleaning_equipment_command.blocked ||
              telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY
            }
            disabledVariant={
              telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY ? 'inactive' : undefined
            }
            onChange={e => onLockChanged(ROBOT_EQUIPMENT_COMMAND.CLEANING_EQUIPMENT, !e.target.checked)}
          />
        </div>
      </div>
      <div className={style.tableLockRow}>
        <div className={style.textAlignLeft}>Watering</div>
        <div className={style.alignRight}>
          <SwitchStyled
            checked={
              telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY
                ? false
                : statuses.watering_command.value
            }
            disabled={
              statuses.watering_command.blocked ||
              telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY
            }
            disabledVariant={
              telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY ? 'inactive' : undefined
            }
            onChange={e => onLockChanged(ROBOT_EQUIPMENT_COMMAND.WATER, e.target.checked)}
          />
        </div>
      </div>
      <div className={style.tableLockRow}>
        <div className={style.textAlignLeft}>Light</div>
        <div className={style.alignRight}>
          <SwitchStyled
            checked={
              telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY
                ? false
                : statuses.light_command.value
            }
            disabled={
              statuses.light_command.blocked || telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY
            }
            disabledVariant={
              telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY ? 'inactive' : undefined
            }
            onChange={e => onLockChanged(ROBOT_EQUIPMENT_COMMAND.LIGHT, e.target.checked)}
          />
        </div>
      </div>
      {emergencyRow}
      {showCleaningModeControl && (
        <div className={style.tableLockRow}>
          <div className={style.textAlignLeft}>Cleaning mode</div>
          <div className={style.alignRight}>
            <Dropdown
              className={style.textfield}
              disabled={!isOnline || cleaningModeBlocked}
              currentOption={cleaningModeOption}
              options={cleaningModeOptions}
              onClickOption={onCleaningModeSelected}
              minWidth="max-content"
            />
          </div>
        </div>
      )}
      <MotorRpmSlider
        key={`turbine-rpm-${robot.robotInfo?.id ?? 'none'}`}
        label="Turbine speed"
        min={TURBINE_RPM_MIN}
        max={TURBINE_RPM_MAX}
        step={50}
        telemetryRpm={telemetry?.vcu?.turbine_motor?.speed}
        disabled={motorControlsDisabled}
        onCommit={rpm => onMotorRpmCommitted('turbine', rpm)}
      />
      {isComboOrOmniRobot && (
        <MotorRpmSlider
          key={`scrubber-rpm-${robot.robotInfo?.id ?? 'none'}`}
          label="Scrubber brush speed"
          min={SCRUBBER_BRUSH_RPM_MIN}
          max={SCRUBBER_BRUSH_RPM_MAX}
          step={50}
          telemetryRpm={telemetry?.vcu?.scrubber_brush_motor?.speed}
          disabled={motorControlsDisabled}
          onCommit={rpm => onMotorRpmCommitted('scrubber_brush', rpm)}
        />
      )}
    </div>
  )
}

export default RobotControl
